import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CheckCircle, Mail, Info, DollarSign, ChevronDown, ChevronUp, Calculator, Camera, AlertTriangle, Navigation, MapPin, Sparkles, RefreshCw, Clock } from 'lucide-react';
import Modal from '../Modal';
import { formatCurrency } from '../../utils/helpers';
import { useBusinessContext } from '../../contexts/BusinessContext';
import JobPhotos from './JobPhotos';
import jobPhotoService from '../../services/jobPhotoService';
import api from '../../services/api';

/**
 * Modal for completing a job with final amount, date, notes, and optional on-site payment
 * 
 * NEW: Added transaction type selection (Business/Personal/Refund) matching KevoFlow pattern
 * 
 * PAYMENT LOGIC:
 * - "Final Amount" = what the customer paid (or will be invoiced)
 * - If "Paid on site" is checked:
 *   - Final Amount IS the total collected - no additional tax added
 *   - "Sales tax was included" checkbox determines how invoice is broken down:
 *     - Unchecked: Subtotal = Final Amount, Tax = $0
 *     - Checked: Back-calculate tax from Final Amount (e.g., $615 = $577.46 + $37.54 tax)
 * 
 * When "Paid on site" is checked, completing the job will:
 * 1. Mark job as Completed
 * 2. Create Invoice (with or without tax breakdown based on checkbox)
 * 3. Create Payment record for the Final Amount
 * 4. Mark Invoice as Paid
 * 5. Optionally send receipt email
 */
const CompleteJobModal = ({
  isOpen,
  onClose,
  job,
  formData,
  setFormData,
  onSubmit,
  emailSettings,
  taxSettings, // { collectsSalesTax, salesTaxRate }
  // New props for "What's Next" feature
  remainingJobsToday = [],
  optimizedRoute = null, // { optimizedJobs: [...], timestamp }
  onNavigateToNext,
  onReoptimize,
  isReoptimizing = false,
}) => {
  const { user, canAccessFeature, isSimpleMode } = useBusinessContext();
  const isOwner = user?.role === 'Owner';
  const hasRouteAccess = canAccessFeature?.('route-optimization') ?? false;
  
  // Success state - shows "What's Next" after completion
  const [showSuccess, setShowSuccess] = useState(false);
  const [completionResult, setCompletionResult] = useState(null);
  const [invoiceBusy, setInvoiceBusy] = useState(null);
  const [invoiceNote, setInvoiceNote] = useState('');
  
  // Local state for paid on site section
  const [paidOnSiteExpanded, setPaidOnSiteExpanded] = useState(isSimpleMode);
  
  // Photo state
  const [photoSummary, setPhotoSummary] = useState(null);
  const [showPhotoSection, setShowPhotoSection] = useState(false);

  // Track if we've initialized for this job (to avoid resetting on data refresh)
  const [initializedForJobId, setInitializedForJobId] = useState(null);

  // Initialize payment fields when modal opens or job changes
  useEffect(() => {
    if (isOpen && job) {
      // Only reset if this is a NEW job (different ID than what we initialized for)
      // This prevents resetting when loadData() refreshes the job object
      if (initializedForJobId !== job.id) {
        setShowSuccess(false);
        setInitializedForJobId(job.id);
        
        // Set defaults for payment fields
        setFormData(prev => ({
          ...prev,
          paidOnSite: prev.paidOnSite ?? false,
          paymentMethod: prev.paymentMethod ?? 'Cash',
          paymentReference: prev.paymentReference ?? '',
          sendReceiptEmail: prev.sendReceiptEmail ?? (emailSettings?.sendPaymentReceived ?? false),
          salesTaxCollected: prev.salesTaxCollected ?? (taxSettings?.collectsSalesTax ?? false),
          transactionType: prev.transactionType ?? 'Business', // NEW: Default transaction type
        }));
        
        // Load photo summary
        loadPhotoSummary();
      }
    }
  }, [isOpen, job?.id, initializedForJobId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Delay reset to allow close animation
      const timer = setTimeout(() => {
        setShowSuccess(false);
        setInitializedForJobId(null); // Reset so next open will initialize fresh
        setCompletionResult(null);
        setInvoiceNote('');
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Auto-expand paid section when checked
  useEffect(() => {
    if (formData.paidOnSite) {
      setPaidOnSiteExpanded(true);
    }
  }, [formData.paidOnSite]);

  const loadPhotoSummary = async () => {
    if (!job?.id) return;
    try {
      const response = await jobPhotoService.getJobPhotoSummary(job.id);
      setPhotoSummary(response.data);
    } catch (err) {
      console.log('Could not load photo summary:', err);
    }
  };

  if (!job) return null;

  // Handle form submit - show success state after
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = await onSubmit();
      // If onSubmit returns false or throws, don't show success
      // Otherwise, always show success state
      if (result !== false) {
        setCompletionResult(result && typeof result === 'object' ? result : null);
        setShowSuccess(true);
      }
    } catch (err) {
      // Error handled by parent, don't show success
      console.error('Error completing job:', err);
    }
  };

  // Handle close - reset and close
  const handleClose = () => {
    setShowSuccess(false);
    onClose();
  };

  // Get next job from remaining jobs (sorted by scheduled time)
  const getNextJob = () => {
    if (remainingJobsToday.length === 0) return null;
    
    // If we have an optimized route, use that order
    if (optimizedRoute?.optimizedJobs?.length > 0) {
      // Find first remaining job in optimized order
      for (const optJob of optimizedRoute.optimizedJobs) {
        const found = remainingJobsToday.find(j => j.id === optJob.jobId);
        if (found) return found;
      }
    }
    
    // Otherwise, return first job sorted by scheduled time
    return [...remainingJobsToday].sort((a, b) => {
      const timeA = getJobTimeForSort(a);
      const timeB = getJobTimeForSort(b);
      return timeA.localeCompare(timeB);
    })[0];
  };

  // Format time for display - extract time directly from scheduledDate string
  // Times are stored as LOCAL time in the format "2026-01-09T15:00:00"
  // Midnight (00:00) means "no time set" - flexible scheduling
  const formatJobTime = (job) => {
    if (!job?.scheduledDate) return null;
    try {
      // Extract time portion directly from the ISO string
      const timePart = job.scheduledDate.split('T')[1];
      if (!timePart) return null;
      
      const [hours, minutes] = timePart.split(':').map(Number);
      
      // Midnight = no time set (flex job), don't display a time
      if (hours === 0 && minutes === 0) return null;
      
      // Format as 12-hour time
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const hour12 = hours % 12 || 12;
      return `${hour12}:${String(minutes).padStart(2, '0')} ${ampm}`;
    } catch {
      return null;
    }
  };

  // Get time string for sorting (HH:MM format)
  // Jobs with specific times sort first (by time), flex jobs (midnight) sort last
  const getJobTimeForSort = (job) => {
    if (!job?.scheduledDate) return '99:99'; // No date = last
    const timePart = job.scheduledDate.split('T')[1];
    if (!timePart) return '99:99';
    const [hours, minutes] = timePart.split(':');
    // Midnight (00:00) = flex job, sort AFTER timed jobs
    if (parseInt(hours) === 0 && parseInt(minutes) === 0) return '99:99';
    return `${hours}:${minutes}`;
  };

  // Check if optimization is stale (> 2 hours old)
  const isOptimizationStale = () => {
    if (!optimizedRoute?.timestamp) return true;
    const twoHoursAgo = Date.now() - (2 * 60 * 60 * 1000);
    return new Date(optimizedRoute.timestamp).getTime() < twoHoursAgo;
  };

  // ============================================
  // SUCCESS STATE - "What's Next" View
  // ============================================
  if (showSuccess && isSimpleMode) {
    const inv = completionResult;
    const hasInvoice = !!inv?.invoiceId;
    const isPaid = !!formData.paidOnSite;
    const noteOk = /^Invoice (texted|emailed)/.test(invoiceNote);
    const textSent = invoiceNote.startsWith('Invoice texted');

    const handleDownloadPdf = async () => {
      setInvoiceBusy('pdf');
      setInvoiceNote('');
      try {
        const res = await api.get(`/invoices/${inv.invoiceId}/pdf`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `Invoice_${inv.invoiceNumber || inv.invoiceId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => window.URL.revokeObjectURL(url), 10000);
      } catch (err) {
        setInvoiceNote('Could not download the PDF. Try again from the Invoices page.');
      } finally {
        setInvoiceBusy(null);
      }
    };

    const handleTextInvoice = async () => {
      setInvoiceBusy('sms');
      setInvoiceNote('');
      try {
        await api.post(`/invoices/${inv.invoiceId}/send-sms`);
        setInvoiceNote(`Invoice texted to ${job.customer.name}`);
      } catch (err) {
        setInvoiceNote(err.response?.data?.message || 'Could not text the invoice.');
      } finally {
        setInvoiceBusy(null);
      }
    };

    const handleEmailInvoice = async () => {
      setInvoiceBusy('email');
      setInvoiceNote('');
      try {
        await api.post(`/invoices/${inv.invoiceId}/send`);
        setInvoiceNote(`Invoice emailed to ${job.customer.email}`);
      } catch (err) {
        setInvoiceNote(err.response?.data?.message || 'Could not email the invoice.');
      } finally {
        setInvoiceBusy(null);
      }
    };

    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="" size="md">
        <div className="text-center py-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Job Completed!</h2>
          <p className="text-gray-500 mb-6">{job.customer?.name}</p>

          {hasInvoice ? (
            <>
              <div className="bg-gray-50 rounded-xl p-4 mb-4 text-left">
                <p className="text-sm text-gray-500">Invoice {inv.invoiceNumber}</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(inv.totalAmount)}</p>
                <p className={`text-sm font-medium mt-1 ${isPaid ? 'text-green-700' : 'text-amber-700'}`}>
                  {isPaid ? 'Paid' : 'Unpaid. Send or print it for the customer.'}
                </p>
              </div>

              <div className="space-y-3 mb-3">
                {job.customer?.phone && (
                  <button
                    onClick={handleTextInvoice}
                    disabled={invoiceBusy !== null}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-xl disabled:opacity-50"
                  >
                    {invoiceBusy === 'sms' ? 'Sending...' : textSent ? '✓ Texted. Tap to send again' : 'Text Invoice'}
                  </button>
                )}
                <button
                  onClick={handleDownloadPdf}
                  disabled={invoiceBusy !== null}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-xl disabled:opacity-50"
                >
                  {invoiceBusy === 'pdf' ? 'Preparing PDF...' : 'Download / Print PDF'}
                </button>
                {job.customer?.email && (
                  <button
                    onClick={handleEmailInvoice}
                    disabled={invoiceBusy !== null}
                    className="w-full bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium py-3 px-4 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Mail size={16} />
                    {invoiceBusy === 'email' ? 'Sending...' : 'Email Invoice'}
                  </button>
                )}
              </div>
              {invoiceNote && (
                <p className={`text-sm font-medium mb-3 ${noteOk ? 'text-green-700' : 'text-red-600'}`}>
                  {noteOk ? '✓ ' : ''}{invoiceNote}
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-600 mb-4">Saved. No invoice was needed for this job.</p>
          )}

          <button
            onClick={handleClose}
            className="w-full text-gray-500 hover:text-gray-700 font-medium py-2"
          >
            Done
          </button>
        </div>
      </Modal>
    );
  }

  if (showSuccess) {
    const nextJob = getNextJob();
    const nextJobTime = nextJob ? formatJobTime(nextJob) : null;
    const showReoptimize = hasRouteAccess && remainingJobsToday.length >= 2;
    const staleOptimization = isOptimizationStale();
    const hasRemainingJobs = remainingJobsToday.length > 0;
    
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title=""
        size="md"
      >
        <div className="text-center py-4">
          {/* Success Icon */}
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-green-600" />
          </div>
          
          <h2 className="text-xl font-bold text-gray-900 mb-1">Job Completed!</h2>
          <p className="text-gray-500 mb-6">{job.customer?.name}</p>
          
          {/* Remaining Jobs Card - only show if there are jobs */}
          {hasRemainingJobs ? (
            <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">
                  📍 {remainingJobsToday.length} job{remainingJobsToday.length !== 1 ? 's' : ''} remaining today
                </span>
              </div>
              
              {nextJob && (
                <div className="bg-white rounded-lg border border-gray-200 p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">
                        {nextJob.customer?.name || 'Unknown'}
                      </p>
                      <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                        <MapPin size={12} />
                        {nextJob.address}
                      </p>
                    </div>
                    <div className="text-right">
                      {nextJobTime ? (
                        <span className="text-sm font-medium text-blue-600 flex items-center gap-1">
                          <Clock size={12} />
                          {nextJobTime}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Flexible</span>
                      )}
                    </div>
                  </div>
                  {isOwner && nextJob.quoteAmount > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-100">
                      <span className="text-sm font-medium text-green-600">
                        {formatCurrency(nextJob.quoteAmount)}
                      </span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Stale optimization hint */}
              {showReoptimize && staleOptimization && (
                <p className="text-xs text-amber-600 mt-3 flex items-center gap-1">
                  <Info size={12} />
                  Route was optimized a while ago - traffic may have changed
                </p>
              )}
            </div>
          ) : (
            /* No remaining jobs - show completion message */
            <div className="bg-green-50 rounded-xl p-4 mb-6">
              <p className="text-green-800 font-medium">🎉 All done for today!</p>
              <p className="text-sm text-green-600 mt-1">Great work - no more jobs scheduled.</p>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="space-y-3">
            {/* Primary: Navigate to Next - only if there are jobs */}
            {hasRemainingJobs && nextJob && (
              <button
                onClick={() => {
                  onNavigateToNext?.(nextJob);
                  handleClose();
                }}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors"
              >
                <Navigation size={18} />
                Navigate to Next Job
              </button>
            )}
            
            {/* Secondary: Re-optimize (Pro feature, 2+ jobs) */}
            {showReoptimize && (
              <button
                onClick={() => {
                  onReoptimize?.();
                }}
                disabled={isReoptimizing}
                className="w-full bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
              >
                {isReoptimizing ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Optimizing...
                  </>
                ) : (
                  <>
                    <Sparkles size={16} />
                    Re-optimize Route
                  </>
                )}
              </button>
            )}
            
            {/* Done button */}
            <button
              onClick={handleClose}
              className={`w-full font-medium py-2 transition-colors ${
                hasRemainingJobs 
                  ? 'text-gray-500 hover:text-gray-700' 
                  : 'bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-xl'
              }`}
            >
              Done
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // ============================================
  // NORMAL FORM STATE
  // ============================================

  // The Final Amount is what the customer paid (or will pay)
  const finalAmount = parseFloat(formData.finalAmount) || job.quoteAmount || 0;
  const finalAmountDiff = formData.finalAmount 
    ? parseFloat(formData.finalAmount) - (job.quoteAmount || 0)
    : 0;

  // Tax rate from settings
  const taxRate = taxSettings?.salesTaxRate || 0;
  const taxRateDecimal = taxRate > 1 ? taxRate / 100 : taxRate;
  const businessCollectsTax = taxSettings?.collectsSalesTax === true && taxRate > 0;

  // Calculate what the invoice breakdown would look like
  // If paid on site: Final Amount is what they paid, we back-calculate if tax was collected
  // If NOT paid on site: We ADD tax to create the invoice total
  let invoiceSubtotal, invoiceTax, invoiceTotal;
  
  if (formData.paidOnSite) {
    // PAID ON SITE: Final Amount is what they paid
    invoiceTotal = finalAmount;
    
    if (formData.salesTaxCollected && businessCollectsTax) {
      // Back-calculate: total = subtotal * (1 + taxRate)
      // So: subtotal = total / (1 + taxRate)
      invoiceSubtotal = Math.round((finalAmount / (1 + taxRateDecimal)) * 100) / 100;
      invoiceTax = Math.round((finalAmount - invoiceSubtotal) * 100) / 100;
    } else {
      // No tax collected - full amount is subtotal
      invoiceSubtotal = finalAmount;
      invoiceTax = 0;
    }
  } else {
    // NOT PAID ON SITE: Standard invoice - add tax if business collects it
    invoiceSubtotal = finalAmount;
    if (businessCollectsTax) {
      invoiceTax = Math.round((finalAmount * taxRateDecimal) * 100) / 100;
    } else {
      invoiceTax = 0;
    }
    invoiceTotal = invoiceSubtotal + invoiceTax;
  }

  const customerHasEmail = !!job.customer?.email;
  
  // Check if after photos are missing
  const hasAfterPhotos = photoSummary?.afterCount > 0;
  const hasAnyPhotos = photoSummary?.totalCount > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Complete Job"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Job Summary */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-2">
            {job.customer?.name}
          </h3>
          <p className="text-sm text-gray-600">{job.address}</p>
          {job.jobServices?.length > 0 && (
            <p className="text-sm text-gray-500 mt-1">
              {job.jobServices.map(js => js.serviceType?.name).filter(Boolean).join(', ')}
            </p>
          )}
          {/* OWNER ONLY: Quote Amount */}
          {isOwner && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <span className="text-sm text-gray-600">Quote Amount: </span>
              <span className="font-semibold text-gray-900">
                {formatCurrency(job.quoteAmount)}
              </span>
            </div>
          )}
        </div>

        {/* NEW: Transaction Type Selection */}
        <div className={`bg-gray-900 border border-gray-700 rounded-lg p-4 ${isSimpleMode ? 'hidden' : ''}`}>
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-lg font-semibold text-white">Transaction Type</h3>
            <TransactionTypeInfoTooltip />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <TransactionTypeButton
              type="Business"
              label="Business Income"
              description="Standard paid service"
              selected={formData.transactionType === 'Business'}
              onClick={() => setFormData(prev => ({ ...prev, transactionType: 'Business' }))}
              variant="blue"
            />
            <TransactionTypeButton
              type="Personal"
              label="Personal/Free"
              description="Family or friends (no charge)"
              selected={formData.transactionType === 'Personal'}
              onClick={() => setFormData(prev => ({ ...prev, transactionType: 'Personal' }))}
              variant="purple"
            />
            <TransactionTypeButton
              type="Refund"
              label="Refund"
              description="Money returned to customer"
              selected={formData.transactionType === 'Refund'}
              onClick={() => setFormData(prev => ({ ...prev, transactionType: 'Refund' }))}
              variant="orange"
            />
          </div>

          {/* Warning for non-business transactions */}
          {formData.transactionType !== 'Business' && (
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
              <Info size={18} className="text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                {formData.transactionType === 'Personal' && (
                  <>
                    <strong>Personal/Free services</strong> are tracked for your records but won't count as taxable income. Perfect for family, friends, or promotional services.
                  </>
                )}
                {formData.transactionType === 'Refund' && (
                  <>
                    <strong>Refunds</strong> reduce your taxable income. Use this when returning money to a customer for a previously paid service.
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Photo Documentation Section */}
        <div className="border rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setShowPhotoSection(!showPhotoSection)}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Camera size={20} className="text-gray-500" />
              <div className="text-left">
                <p className="font-medium text-gray-900">Job Photos</p>
                <p className="text-sm text-gray-500">
                  {photoSummary ? (
                    <>
                      {photoSummary.totalCount} photo{photoSummary.totalCount !== 1 ? 's' : ''}
                      {photoSummary.beforeCount > 0 && ` • ${photoSummary.beforeCount} before`}
                      {photoSummary.afterCount > 0 && ` • ${photoSummary.afterCount} after`}
                    </>
                  ) : (
                    'Document your completed work'
                  )}
                </p>
              </div>
            </div>
            
            {/* Status indicator */}
            <div className="flex items-center gap-2">
              {photoSummary && (
                <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                  hasAfterPhotos 
                    ? 'bg-green-100 text-green-700'
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {hasAfterPhotos ? (
                    <>
                      <CheckCircle size={12} />
                      After photos added
                    </>
                  ) : (
                    <>
                      <AlertTriangle size={12} />
                      No after photos
                    </>
                  )}
                </div>
              )}
              {showPhotoSection ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </button>
          
          {showPhotoSection && (
            <div className="p-4 border-t">
              {/* Recommendation for after photos */}
              {photoSummary && !hasAfterPhotos && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Pro tip:</strong> Adding "After" photos helps showcase your work 
                    and can be used for marketing and customer follow-ups.
                  </p>
                </div>
              )}
              
              <JobPhotos
                jobId={job.id}
                onPhotoCountChange={() => loadPhotoSummary()}
              />
            </div>
          )}
        </div>

        {/* OWNER ONLY: Final Amount */}
        {isOwner && (
          <div>
            <label className="label">
              Final Amount
              <span className="ml-2 text-xs text-gray-500 font-normal">
                {formData.paidOnSite 
                  ? '(what customer actually paid)'
                  : '(adjust if different from quote)'
                }
              </span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
              <input
                type="number"
                step="0.01"
                value={formData.finalAmount}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  finalAmount: e.target.value 
                })}
                className="input pl-7"
                placeholder={job.quoteAmount?.toString() || '0.00'}
              />
            </div>
            {formData.finalAmount && finalAmountDiff !== 0 && (
              <p className={`text-xs mt-1 ${finalAmountDiff > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                {finalAmountDiff > 0 
                  ? `+${formatCurrency(finalAmountDiff)} from quote`
                  : `${formatCurrency(finalAmountDiff)} from quote`
                }
              </p>
            )}
          </div>
        )}

        {/* Completed Date */}
        <div>
          <label className="label">Completed Date</label>
          <input
            type="date"
            value={formData.completedDate}
            onChange={(e) => setFormData({ 
              ...formData, 
              completedDate: e.target.value 
            })}
            className="input"
            max={new Date().toISOString().split('T')[0]}
          />
        </div>

        {/* Completion Notes */}
        <div>
          <label className="label">
            Completion Notes
            <span className="ml-2 text-xs text-gray-500 font-normal">(optional)</span>
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ 
              ...formData, 
              notes: e.target.value 
            })}
            className="input"
            rows="2"
            placeholder="Any notes about the completed work..."
          />
        </div>

        {/* OWNER ONLY: Paid On Site Section - only show for Business transactions */}
        {isOwner && formData.transactionType === 'Business' && (
          <div className="border-2 border-dashed border-green-300 rounded-lg overflow-hidden">
            {/* Header - always visible */}
            <button
              type="button"
              onClick={() => setPaidOnSiteExpanded(!paidOnSiteExpanded)}
              className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${
                formData.paidOnSite 
                  ? 'bg-green-100' 
                  : 'bg-green-50 hover:bg-green-100'
              }`}
            >
              <div className="flex items-center gap-3">
                <DollarSign className={`w-5 h-5 ${formData.paidOnSite ? 'text-green-600' : 'text-green-500'}`} />
                <div className="text-left">
                  <p className="font-medium text-gray-900">Customer paid on site</p>
                  <p className="text-xs text-gray-600">
                    {formData.paidOnSite 
                      ? `${formData.paymentMethod} • ${formatCurrency(finalAmount)}`
                      : 'Skip invoicing - record payment now'
                    }
                  </p>
                </div>
              </div>
              {paidOnSiteExpanded ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {/* Expanded content */}
            {paidOnSiteExpanded && (
              <div className="px-4 py-3 bg-white border-t border-green-200 space-y-3">
                {/* Checkbox */}
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.paidOnSite || false}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      paidOnSite: e.target.checked 
                    })}
                    className="w-5 h-5 text-green-600 rounded border-gray-300 focus:ring-green-500"
                  />
                  <span className="font-medium text-gray-900">Yes, customer already paid</span>
                </label>

                {/* Payment details - only show when checked */}
                {formData.paidOnSite && (
                  <div className="space-y-3 pt-2">
                    {/* Amount collected */}
                    <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-green-900">Amount Collected:</span>
                        <span className="text-lg font-bold text-green-700">{formatCurrency(finalAmount)}</span>
                      </div>
                      <p className="text-xs text-green-600 mt-1">
                        This is what the customer handed you (from Final Amount above)
                      </p>
                    </div>

                    {/* Sales Tax Collected - Only show if business collects tax */}
                    {businessCollectsTax && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.salesTaxCollected || false}
                            onChange={(e) => setFormData({ 
                              ...formData, 
                              salesTaxCollected: e.target.checked 
                            })}
                            className="mt-0.5 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <span className="text-sm font-medium text-blue-900 flex items-center gap-1.5">
                              <Calculator size={14} className="text-blue-600" />
                              Sales tax was included in this amount
                            </span>
                            <p className="text-xs text-blue-700 mt-1">
                              Check this if the {formatCurrency(finalAmount)} includes {(taxRateDecimal * 100).toFixed(2)}% sales tax
                            </p>
                          </div>
                        </label>
                        
                        {/* Show tax breakdown preview */}
                        <div className="mt-3 pt-2 border-t border-blue-200 text-sm">
                          <p className="text-xs font-medium text-blue-800 mb-2">Invoice will show:</p>
                          <div className="space-y-1">
                            <div className="flex justify-between text-blue-700">
                              <span>Subtotal:</span>
                              <span>{formatCurrency(invoiceSubtotal)}</span>
                            </div>
                            <div className="flex justify-between text-blue-700">
                              <span>Tax ({(taxRateDecimal * 100).toFixed(2)}%):</span>
                              <span>{formData.salesTaxCollected ? formatCurrency(invoiceTax) : '$0.00'}</span>
                            </div>
                            <div className="flex justify-between font-semibold text-blue-900 pt-1 border-t border-blue-200">
                              <span>Total:</span>
                              <span>{formatCurrency(finalAmount)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Payment method and reference */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="label text-sm">Payment Method</label>
                        <select
                          value={formData.paymentMethod || 'Cash'}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            paymentMethod: e.target.value 
                          })}
                          className="input"
                        >
                          <option value="Cash">Cash</option>
                          <option value="Check">Check</option>
                          <option value="Credit Card">Credit Card</option>
                          <option value="Debit Card">Debit Card</option>
                          <option value="Venmo">Venmo</option>
                          <option value="Zelle">Zelle</option>
                          <option value="PayPal">PayPal</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="label text-sm">Reference #</label>
                        <input
                          type="text"
                          value={formData.paymentReference || ''}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            paymentReference: e.target.value 
                          })}
                          className="input"
                          placeholder="Check #, etc."
                        />
                      </div>
                    </div>

                    {/* Send receipt email */}
                    {customerHasEmail ? (
                      <label className="flex items-start gap-3 cursor-pointer p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <input
                          type="checkbox"
                          checked={formData.sendReceiptEmail || false}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            sendReceiptEmail: e.target.checked 
                          })}
                          className="mt-0.5 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                            <Mail size={14} className="text-blue-600" />
                            Send payment receipt
                          </span>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Email {job.customer.email}
                          </p>
                        </div>
                      </label>
                    ) : (
                      <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <Info size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-amber-800">
                          No customer email on file. No receipt will be sent.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Send Completion Email - only show if NOT paying on site OR not Business transaction */}
        {(!formData.paidOnSite || formData.transactionType !== 'Business') && (
          customerHasEmail ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.sendCompletedEmail}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    sendCompletedEmail: e.target.checked 
                  })}
                  className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                    <Mail size={14} className="text-blue-600" />
                    Send completion notification
                  </span>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Email {job.customer.email}
                  </p>
                </div>
              </label>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Info size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-900">
                  No customer email on file. No notification will be sent.
                </p>
              </div>
            </div>
          )
        )}

        {/* What happens next - dynamic based on selections */}
        <div className={`rounded-lg p-3 border ${
          formData.paidOnSite && formData.transactionType === 'Business'
            ? 'bg-green-50 border-green-200' 
            : formData.transactionType === 'Personal'
            ? 'bg-purple-50 border-purple-200'
            : formData.transactionType === 'Refund'
            ? 'bg-orange-50 border-orange-200'
            : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex items-start gap-2">
            <CheckCircle size={16} className={`mt-0.5 flex-shrink-0 ${
              formData.transactionType === 'Personal' ? 'text-purple-600' 
              : formData.transactionType === 'Refund' ? 'text-orange-600'
              : formData.paidOnSite ? 'text-green-600' : 'text-gray-500'
            }`} />
            <div>
              <p className={`text-sm font-medium ${
                formData.transactionType === 'Personal' ? 'text-purple-900'
                : formData.transactionType === 'Refund' ? 'text-orange-900'
                : formData.paidOnSite ? 'text-green-900' : 'text-gray-900'
              }`}>
                What happens next:
              </p>
              <ul className={`text-xs mt-1 space-y-0.5 ${
                formData.transactionType === 'Personal' ? 'text-purple-800'
                : formData.transactionType === 'Refund' ? 'text-orange-800'
                : formData.paidOnSite ? 'text-green-800' : 'text-gray-600'
              }`}>
                <li>• Job will be marked as Completed</li>
                
                {formData.transactionType === 'Personal' ? (
                  <>
                    <li>• Recorded as <strong>Personal/Free service</strong> (not taxable income)</li>
                    <li>• No invoice will be created</li>
                    <li>• Tracked for your records and portfolio</li>
                  </>
                ) : formData.transactionType === 'Refund' ? (
                  <>
                    <li>• Recorded as <strong>Refund</strong> (reduces taxable income)</li>
                    <li>• Refund payment record will be created</li>
                    <li>• Will appear in tax reports as income reduction</li>
                  </>
                ) : formData.paidOnSite ? (
                  <>
                    <li>• Invoice will be created for {formatCurrency(finalAmount)}
                      {formData.salesTaxCollected && businessCollectsTax && (
                        <span className="text-green-600"> (includes {formatCurrency(invoiceTax)} tax)</span>
                      )}
                    </li>
                    <li>• Payment of {formatCurrency(finalAmount)} ({formData.paymentMethod || 'Cash'}) will be recorded</li>
                    <li>• Invoice will be marked as <strong>Paid</strong></li>
                    {formData.sendReceiptEmail && customerHasEmail && (
                      <li>• Payment receipt will be emailed to customer</li>
                    )}
                  </>
                ) : (
                  <>
                    {isOwner && (
                      <li>• It will appear in "Ready to Invoice" on the Invoices page
                        {businessCollectsTax && (
                          <span className="text-gray-500"> (invoice will add {(taxRateDecimal * 100).toFixed(2)}% tax)</span>
                        )}
                      </li>
                    )}
                    {formData.sendCompletedEmail && customerHasEmail && (
                      <li>• Customer will receive a completion email</li>
                    )}
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
          <button
            type="button"
            onClick={handleClose}
            className="btn btn-secondary flex-1 justify-center"
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className={`btn flex-1 justify-center flex items-center gap-2 ${
              formData.transactionType === 'Personal' 
                ? 'bg-purple-600 hover:bg-purple-700 text-white'
                : formData.transactionType === 'Refund'
                ? 'bg-orange-600 hover:bg-orange-700 text-white'
                : formData.paidOnSite && formData.transactionType === 'Business'
                ? 'bg-green-600 hover:bg-green-700 text-white' 
                : 'btn-primary'
            }`}
          >
            <CheckCircle size={18} />
            {formData.transactionType === 'Personal' ? 'Complete Personal Service'
             : formData.transactionType === 'Refund' ? 'Complete Refund'
             : formData.paidOnSite ? 'Complete & Record Payment' 
             : 'Complete Job'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ============================================
// TRANSACTION TYPE COMPONENTS
// ============================================

function TransactionTypeButton({ type, label, description, selected, onClick, variant }) {
  const variantClasses = {
    blue: selected 
      ? 'bg-blue-600 border-blue-500 text-white' 
      : 'bg-gray-900 border-gray-700 hover:border-gray-600 text-white hover:bg-gray-800',
    purple: selected 
      ? 'bg-purple-600 border-purple-500 text-white' 
      : 'bg-gray-900 border-gray-700 hover:border-gray-600 text-white hover:bg-gray-800',
    orange: selected 
      ? 'bg-orange-600 border-orange-500 text-white' 
      : 'bg-gray-900 border-gray-700 hover:border-gray-600 text-white hover:bg-gray-800'
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`p-4 rounded-lg border-2 text-left transition-all ${variantClasses[variant]}`}
    >
      <div className="font-semibold mb-1">{label}</div>
      <div className={`text-xs ${
        selected 
          ? 'text-white opacity-90' 
          : 'text-gray-300'
      }`}>
        {description}
      </div>
    </button>
  );
}

function TransactionTypeInfoTooltip() {
  const [showInfo, setShowInfo] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onMouseEnter={() => setShowInfo(true)}
        onMouseLeave={() => setShowInfo(false)}
        className="text-gray-400 hover:text-gray-300"
      >
        <Info size={18} />
      </button>
      
      {showInfo && (
        <div className="absolute left-0 top-full mt-2 w-80 bg-gray-900 border border-gray-700 rounded-lg p-4 shadow-xl z-10 text-sm">
          <div className="space-y-3">
            <div>
              <div className="font-semibold text-blue-400">Business Income</div>
              <div className="text-gray-300">Standard paid services. Included in tax reports as taxable income.</div>
            </div>
            <div>
              <div className="font-semibold text-purple-400">Personal/Free</div>
              <div className="text-gray-300">Services for family/friends at no charge. Tracked for records but NOT counted as taxable income.</div>
            </div>
            <div>
              <div className="font-semibold text-orange-400">Refund</div>
              <div className="text-gray-300">Money returned to customer. Reduces your taxable income appropriately.</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompleteJobModal;